/**
 * The phase frame: its default, its validated read and its one edit.
 *
 * Pure. An absent or unreadable frame is COMBAT, which is the whole app as
 * it was before phases existed - so an old session, a corrupt document and a
 * build that does not know a newer phase all land on a working table.
 */
import { emptyStealth, readStealth } from '../stealth/ladder';
import { readTimer } from '../narrative/timer';
import type { StealthState } from '../stealth/ladder';
import { PHASES } from './types';
import type { Phase, PhaseFrame } from './types';

export const DEFAULT_PHASE: Phase = 'combat';

export const PHASE_LABELS: Record<Phase, string> = {
  narrative: 'Narrative',
  stealth: 'Stealth',
  combat: 'Combat',
};

export const emptyFrame = (): PhaseFrame => ({
  phase: DEFAULT_PHASE,
  partyTeam: null,
  dangerPin: null,
  positions: null,
  beats: 0,
});

const isPhase = (value: unknown): value is Phase => PHASES.includes(value as Phase);
const isPin = (value: unknown): value is 1 | 2 | 3 | 4 => [1, 2, 3, 4].includes(value as number);
const isCount = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 0;
const isTag = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/** A stored frame, validated field by field. Anything it does not recognise reads as the default. */
export function readFrame(raw: unknown): PhaseFrame {
  const stored = (raw ?? {}) as Record<string, unknown>;
  return {
    phase: isPhase(stored.phase) ? stored.phase : DEFAULT_PHASE,
    partyTeam: isTag(stored.partyTeam) ? stored.partyTeam : null,
    dangerPin: isPin(stored.dangerPin) ? stored.dangerPin : null,
    positions: typeof stored.positions === 'boolean' ? stored.positions : null,
    beats: isCount(stored.beats) ? stored.beats : 0,
    ...(stored.stealth && typeof stored.stealth === 'object' ? { stealth: readStealth(stored.stealth) } : {}),
    ...(readTimer(stored.timer) ? { timer: readTimer(stored.timer) } : {}),
  };
}

const NO_STEALTH = Object.freeze(emptyStealth());

/** The frame's ladder, or the empty one when the table has never used Stealth. The same object each time. */
export const stealthOf = (frame: Pick<PhaseFrame, 'stealth'> | null | undefined): StealthState =>
  frame?.stealth ?? NO_STEALTH;

export const withPhase = (frame: PhaseFrame, phase: Phase): PhaseFrame => ({ ...frame, phase });
